import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { useWorld } from "../state/World";
import { IMAGES } from "../lib/tokens";
import { MONORAIL } from "../lib/monorail";
import Plate from "../components/Plate";
import PortalBelow from "../components/PortalBelow";

export const Threshold = () => {
  const { enter, enterAsJudge, portal, portals } = useWorld();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  // Each Portal has its own front door. PlayForward opens on the Arcade: you
  // give an address, the doors open, you walk in. No Atlas, no World.
  const atPortal = portal && portal !== "endeavorum";
  const entryPath = portals.find((p) => p.key === portal)?.entry_path || "/";
  const worldOrigin = portals.find((p) => p.key === "endeavorum")?.origin;

  // Each Practice door stands on its own Place, so the plate and the words come
  // from the Place itself — playforward.app on the Arcade steps, stageward.app
  // on the Hall's. One data row, never a hardcoded pair. The Commons is
  // handled by its own page (routed by App.js on the commons portal), so it
  // never lands here.
  const placeKey = portal === "stageward" ? "hall" : "arcade";
  const place = MONORAIL.find((p) => p.key === placeKey);
  // .com landing = the aerial Ghost World (the World preview, ghost islands
  // teasing what's coming). .app landings unchanged.
  const plate = atPortal ? IMAGES[place.plate] : IMAGES.ghostWorld;

  const go = async (fn, which) => {
    setBusy(which);
    try {
      await fn();
      nav(entryPath, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The World did not open. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <div className="relative min-h-screen overflow-hidden">
        <Plate src={plate} depth={10} />

        {/* brand, offer and promise carried onto every door, so a traveller who
            arrives at a Practice apex meets the same World they would at the root */}
        {atPortal && (
          <motion.div
            className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-col gap-1.5 px-6 pb-10 pt-5 [@media(max-height:880px)]:gap-1 [@media(max-height:880px)]:pb-6 [@media(max-height:880px)]:pt-4 md:px-10"
            style={{
              background:
                "linear-gradient(180deg, rgba(11,7,4,0.97) 0%, rgba(11,7,4,0.92) 46%, rgba(11,7,4,0.72) 74%, rgba(11,7,4,0) 100%)",
            }}
            data-testid="portal-offer"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2 }}
          >
            <p className="mono text-[9px] uppercase tracking-[0.34em] text-[#f2c877] md:text-[10px]">
              Endeavor to Design, Build &amp; Thrive
            </p>
            <h2 className="engrave text-[clamp(1.05rem,2.2vw,1.9rem)] font-extrabold leading-none tracking-[0.02em] text-[#fdf3d8]">
              ADULTING FINALLY HAS A WORLD
            </h2>
            <p className="serif max-w-[560px] text-[13px] font-semibold leading-[1.45] text-[#e2cba2] md:text-[15px]">
              Your <span className="text-[#f8e6b4]">Authentic Intelligence</span> meets{" "}
              <span className="text-[#f8e6b4]">Assistive Intelligence</span> in Places
              to Endeavor. Roll up your sleeves, leave with Blueprints to Execute.
            </p>
          </motion.div>
        )}

        <div
          className={`relative z-10 flex min-h-screen items-center justify-center px-6 ${
            atPortal
              ? "pb-12 pt-32 [@media(max-height:880px)]:pt-24 md:pt-40"
              : "py-20"
          }`}
        >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="w-full max-w-lg"
        >
          <div className="glass rounded-2xl p-6 sm:p-7">
            <img
              src={IMAGES.logoDark}
              alt="Endeavorum — endeavor to build & thrive"
              data-testid="brand-lockup"
              className="mb-4 block w-[150px] [@media(max-height:880px)]:mb-3 [@media(max-height:880px)]:w-[120px] sm:w-[190px]"
              style={{ mixBlendMode: "screen" }}
            />
            <h1 className="serif mb-3 text-2xl font-light leading-[1.08] text-[#f4ecdd] [@media(max-height:880px)]:text-2xl sm:text-4xl">
              {atPortal ? (
                <>
                  {place.name} —
                  <span className="italic text-[var(--atlas-gold)]"> the doors are lit.</span>
                </>
              ) : (
                <>
                  A World for the
                  <span className="italic text-[var(--atlas-gold)]"> work of being an adult.</span>
                </>
              )}
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-white/55 [@media(max-height:880px)]:mb-4 [@media(max-height:880px)]:text-[13px]">
              {atPortal
                ? `${place.tagline} Give an address and the doors open — your Pouch and your MelzAI are already waiting, wherever else in the World you have been.`
                : "Two doors are lit tonight. One asks what you are actually trying to do. The other asks how. The same you, the same Pouch, the same MelzAI walks through both."}
            </p>

            <button
              type="button"
              data-testid="judge-mode-button"
              disabled={busy}
              onClick={() => go(enterAsJudge, "judge")}
              className="btn btn-gold w-full"
            >
              {busy === "judge" ? (
                <Loader2 className="mx-auto animate-spin" size={14} />
              ) : (
                "Enter as Player X · Judge Mode"
              )}
            </button>
            <p className="mono mt-3 text-[10px] leading-relaxed text-white/35">
              A seeded returning traveller with Tokens in the Pouch. Full loop in
              about three minutes, no setup.
            </p>

            <div className="my-5 flex items-center gap-4 [@media(max-height:880px)]:my-4">
              <div className="h-px flex-1 bg-white/10" />
              <span className="tracked text-[9px] text-white/25">or arrive as yourself</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!email.includes("@")) return toast.error("An address the World can reach.");
                go(() => enter(email, name), "self");
              }}
            >
              <input
                className="field"
                data-testid="entry-email-input"
                placeholder="you@somewhere.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                className="field"
                data-testid="entry-name-input"
                placeholder="What should MelzAI call you?"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button
                type="submit"
                data-testid="entry-submit-button"
                disabled={busy}
                className="btn flex w-full items-center justify-center gap-2"
              >
                {busy === "self" ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  <>
                    Cross the threshold <ArrowRight size={13} />
                  </>
                )}
              </button>
            </form>
            {/* Housekeeping note, not an invitation. On a short screen it is the
                first thing to go — a clipped "Cross the threshold" button is a
                far worse first impression than a missing footnote. */}
            <p className="mono mt-4 text-[9px] leading-relaxed text-white/25 [@media(max-height:880px)]:hidden">
              Email-capture session only — no password in V1. Real auth drops in
              behind the same AuthProvider seam.
            </p>
          </div>
        </motion.div>
        </div>

        {atPortal && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40 bg-gradient-to-t from-[var(--ink)] to-transparent" />
        )}
      </div>

      {atPortal && <PortalBelow place={place} worldOrigin={worldOrigin} />}
    </div>
  );
};

export default Threshold;
